import { useState, useCallback, useEffect } from "react";
import { utcIsoToIstDate } from "./utils/date";
import { slotMatchesFixed, slotMatchesDynamic } from "./utils/slotMatch";
import {
  searchProfilesByPhone,
  addProfile,
  UserNotFoundError,
  listAdminAcademies,
  listAdminCenters,
  listAgeCategoriesByCenter,
  listAdminBatches,
  fetchPlanFields,
  addEnrolmentV2,
  updateEnrolmentV2,
  type SearchProfilesData,
  type PlayerProfile,
  type AcademyListItem,
  type CenterListItem,
  type BatchListItem,
  type BatchFee,
  type BatchDynamicSlot,
  type CreateEnrolmentV2Payload,
  type UpdateEnrolmentV2Payload,
  type EnrolmentDetails,
  type BatchDetails,
  type AddProfilePayload,
  type PlanField,
} from "../../../api/academyEnrolments/enrolmentsService";
import { sortAvailablePaymentFrequencies } from "./planFieldsOrdering";

export type EnrolmentFormMode = "add" | "edit" | "view";

export type RegisterBy = "self" | "parent";

export interface SelectedSlot {
  days?: string[];
  day?: string;
  startTime: string;
  endTime: string;
}

export interface EnrolmentFormState {
  // Step 1 – phone + profile
  phoneNumber: string;
  searchedProfileData: SearchProfilesData | null;
  phoneSearched: boolean;
  /** True when the last search returned 404 */
  isNewUser: boolean;
  /** Error message from the 404 response */
  phoneSearchError: string | null;
  /** Controls Add Profile sub-form visibility */
  showAddProfileForm: boolean;

  // New-profile form fields (used by both 404 and "Add New Profile" from existing user)
  newParentName: string;
  newParentGender: string;
  newParentDob: string;
  newPlayerName: string;
  newPlayerGender: string;
  newPlayerDob: string;

  // Step 2 – register by
  registerBy: RegisterBy | "";

  // Step 3 – player selection
  selectedPlayer: PlayerProfile | null;

  // Step 4 – academy + sport
  academyList: AcademyListItem[];
  selectedAcademy: AcademyListItem | null;
  selectedSport: string;

  // Step 5 – center
  centerList: CenterListItem[];
  selectedCenter: CenterListItem | null;

  // Step 6 – age category
  ageCategoryList: string[];
  selectedAgeCategory: string;

  // Step 6 – batch
  batchList: BatchListItem[];
  selectedBatch: BatchListItem | null;

  // Step 7 – slot
  selectedSlot: SelectedSlot | null;          // fixed: single slot
  selectedDynamicSlots: SelectedSlot[];        // dynamic: multiple slots

  // Step 8 – frequencies
  selectedFee: BatchFee | null;
  selectedTrainingFrequency: string;
  selectedPaymentFrequency: string;

  // Step 9 – start date
  startDate: string;

  // Payment amount (₹)
  paymentAmount: string;
}

export interface EnrolmentFormLoading {
  phoneSearch: boolean;
  addProfile: boolean;
  academies: boolean;
  centers: boolean;
  ageCategories: boolean;
  batches: boolean;
  submit: boolean;
}

function buildInitialState(): EnrolmentFormState {
  return {
    phoneNumber: "",
    searchedProfileData: null,
    phoneSearched: false,
    isNewUser: false,
    phoneSearchError: null,
    showAddProfileForm: false,
    newParentName: "",
    newParentGender: "",
    newParentDob: "",
    newPlayerName: "",
    newPlayerGender: "",
    newPlayerDob: "",
    registerBy: "",
    selectedPlayer: null,
    academyList: [],
    selectedAcademy: null,
    selectedSport: "",
    centerList: [],
    selectedCenter: null,
    ageCategoryList: [],
    selectedAgeCategory: "",
    batchList: [],
    selectedBatch: null,
    selectedSlot: null,
    selectedDynamicSlots: [],
    selectedFee: null,
    selectedTrainingFrequency: "",
    selectedPaymentFrequency: "",
    startDate: "",
    paymentAmount: "",
  };
}

function buildInitialLoading(): EnrolmentFormLoading {
  return {
    phoneSearch: false,
    addProfile: false,
    academies: false,
    centers: false,
    ageCategories: false,
    batches: false,
    submit: false,
  };
}

interface UseEnrolmentFormOptions {
  mode: EnrolmentFormMode;
  initialEnrolment?: EnrolmentDetails | null;
  initialBatch?: BatchDetails | null;
  onSuccess?: () => void;
}

export function useEnrolmentForm({
  mode,
  initialEnrolment,
  initialBatch,
  onSuccess,
}: UseEnrolmentFormOptions) {
  const [state, setState] = useState<EnrolmentFormState>(buildInitialState);
  const [loading, setLoading] = useState<EnrolmentFormLoading>(buildInitialLoading);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [planFields, setPlanFields] = useState<PlanField[]>([]);

  useEffect(() => {
    fetchPlanFields()
      .then(setPlanFields)
      .catch(() => setPlanFields([]));
  }, []);

  // ── Hydrate for edit / view ──────────────────────────────────────────────
  useEffect(() => {
    if ((mode === "edit" || mode === "view") && initialEnrolment && initialBatch) {
      const enrolment = initialEnrolment;
      const batch = initialBatch;

      const rawFixedSlot =
        enrolment.fixedSlots?.length > 0
          ? {
              days: enrolment.fixedSlots[0].days,
              startTime: enrolment.fixedSlots[0].startTime,
              endTime: enrolment.fixedSlots[0].endTime,
            }
          : null;

      const rawDynamicSlots: SelectedSlot[] =
        (enrolment.slots as BatchDynamicSlot[])?.length > 0
          ? (enrolment.slots as BatchDynamicSlot[]).map((s) => ({
              day: s.day,
              startTime: s.startTime,
              endTime: s.endTime,
            }))
          : [];

      // Only keep enrolment slots that actually exist in the batch (i.e. are
      // visible as cards in the UI). This prevents phantom slots — present in
      // the enrolment API but absent from the batch API — from riding along in
      // the update payload.
      const visibleFixedSlot =
        rawFixedSlot && batch.fixedSlots?.some((bs) => slotMatchesFixed(rawFixedSlot, bs))
          ? rawFixedSlot
          : null;

      const visibleDynamicSlots = rawDynamicSlots.filter((es) =>
        batch.slots?.some((bs) => slotMatchesDynamic(es, bs))
      );

      const matchingFee =
        batch.fees.find((f) => f.trainingFrequency === enrolment.trainingFrequency) ??
        batch.fees[0] ??
        null;

      setState((prev) => ({
        ...prev,
        phoneNumber: String(enrolment.user?.phoneNumber ?? ""),
        phoneSearched: true,
        isNewUser: false,
        phoneSearchError: null,
        showAddProfileForm: false,
        searchedProfileData: {
          email: null,
          fullName: enrolment.player?.fullName ?? "",
          location: enrolment.coachingCenter?.basicDetails?.location ?? "",
          phoneNumber: enrolment.user?.phoneNumber ?? 0,
          players: [
            {
              _id: enrolment.player?._id ?? "",
              dob: null,
              email: null,
              fullName: enrolment.player?.fullName ?? "",
              gender: null,
              profilePic: enrolment.player?.profilePic ?? null,
              relationshipType: enrolment.enrolledBy === "self" ? "self" : "parent",
            },
          ],
          user: enrolment.user?._id ?? "",
        },
        registerBy: (enrolment.enrolledBy as RegisterBy) ?? "",
        selectedPlayer: {
          _id: enrolment.player?._id ?? "",
          dob: null,
          email: null,
          fullName: enrolment.player?.fullName ?? "",
          gender: null,
          profilePic: enrolment.player?.profilePic ?? null,
          relationshipType: enrolment.enrolledBy === "self" ? "self" : "parent",
        },
        selectedAcademy: enrolment.academy
          ? {
              _id: enrolment.academy._id,
              basicDetails: {
                academyName: enrolment.academy.basicDetails.academyName,
                sports: [enrolment.sports],
              },
            }
          : null,
        selectedSport: enrolment.sports ?? "",
        selectedCenter: enrolment.coachingCenter
          ? {
              _id: enrolment.coachingCenter._id,
              basicDetails: {
                address: enrolment.coachingCenter.basicDetails.address,
                location: enrolment.coachingCenter.basicDetails.location,
              },
            }
          : null,
        ageCategoryList: enrolment.ageCategory ? [enrolment.ageCategory] : [],
        selectedAgeCategory: enrolment.ageCategory ?? batch.ageCategory ?? "",
        selectedBatch: {
          _id: batch._id,
          ageCategory: batch.ageCategory,
          batchName: batch.batchName,
          course: batch.course,
          fees: batch.fees,
          fixedSlots: batch.fixedSlots,
          slotType: batch.slotType,
          slots: batch.slots,
          trainingDays: batch.trainingDays,
        },
        selectedSlot: enrolment.slotType === "fixed" ? visibleFixedSlot : null,
        selectedDynamicSlots: enrolment.slotType === "dynamic" ? visibleDynamicSlots : [],
        selectedFee: matchingFee,
        selectedTrainingFrequency: enrolment.trainingFrequency ?? "",
        selectedPaymentFrequency: enrolment.paymentFrequency ?? "",
        startDate: utcIsoToIstDate(enrolment.startDate),
        paymentAmount: enrolment.totalFeeInfo?.totalFeeAfterGST != null
          ? String(enrolment.totalFeeInfo.totalFeeAfterGST)
          : "",
      }));
    }
  }, [mode, initialEnrolment, initialBatch]);

  // ── Phone Search ─────────────────────────────────────────────────────────
  const handlePhoneSearch = useCallback(async () => {
    const cleaned = state.phoneNumber.trim().replace(/\D/g, "");
    if (!cleaned) return;

    setLoading((l) => ({ ...l, phoneSearch: true }));
    setSubmitError(null);

    try {
      const data = await searchProfilesByPhone(Number(cleaned));
      setState((prev) => ({
        ...prev,
        searchedProfileData: data,
        phoneSearched: true,
        isNewUser: false,
        phoneSearchError: null,
        showAddProfileForm: false,
        registerBy: "",
        selectedPlayer: null,
        selectedAcademy: null,
        selectedSport: "",
        centerList: [],
        selectedCenter: null,
        ageCategoryList: [],
        selectedAgeCategory: "",
        batchList: [],
        selectedBatch: null,
        selectedSlot: null,
        selectedDynamicSlots: [],
        selectedFee: null,
        selectedTrainingFrequency: "",
        selectedPaymentFrequency: "",
        startDate: "",
      }));
    } catch (err: unknown) {
      if (err instanceof UserNotFoundError) {
        // User not found — show error and open the Add Profile form immediately
        setState((prev) => ({
          ...prev,
          searchedProfileData: null,
          phoneSearched: true,
          isNewUser: true,
          phoneSearchError: err.apiMessage,
          showAddProfileForm: true,
          registerBy: "",
          selectedPlayer: null,
          selectedAcademy: null,
          selectedSport: "",
          centerList: [],
          selectedCenter: null,
          ageCategoryList: [],
          selectedAgeCategory: "",
          batchList: [],
          selectedBatch: null,
          selectedSlot: null,
        selectedDynamicSlots: [],
          selectedFee: null,
          selectedTrainingFrequency: "",
          selectedPaymentFrequency: "",
          startDate: "",
        }));
      } else {
        setSubmitError("Failed to search profiles. Please try again.");
      }
    } finally {
      setLoading((l) => ({ ...l, phoneSearch: false }));
    }
  }, [state.phoneNumber]);

  // ── Add new profile ──────────────────────────────────────────────────────
  // Calls /add-profile, derives real user + player IDs from the response,
  // then wires the form up exactly as if we'd found an existing user.
  const handleAddProfile = useCallback(
    async (registerBy: RegisterBy) => {
      const cleaned = state.phoneNumber.trim().replace(/\D/g, "");
      if (!cleaned) return;

      setLoading((l) => ({ ...l, addProfile: true }));
      setSubmitError(null);

      try {
        const payload: AddProfilePayload = {
          phoneNumber: Number(cleaned),
          parent: {
            fullName: state.newParentName,
            gender: state.newParentGender,
            dob: state.newParentDob,
          },
        };
        if (registerBy === "parent") {
          payload.player = {
            fullName: state.newPlayerName,
            gender: state.newPlayerGender,
            dob: state.newPlayerDob,
          };
        }

        const data = await addProfile(payload);

        // The API returns different shapes depending on isExistingUser:
        //   isExistingUser: false → createdProfiles[]  (newly created)
        //   isExistingUser: true  → players[]          (full list of existing profiles)
        const profileList: typeof data.createdProfiles =
          data.isExistingUser
            ? (data.players ?? data.addedProfiles ?? [])
            : (data.createdProfiles ?? []);

        if (!profileList || profileList.length === 0) {
          setSubmitError("Profile created but no profile data was returned. Please refresh and try again.");
          return;
        }

        // Build a synthetic SearchProfilesData so downstream selects / display work
        const syntheticProfileData: SearchProfilesData = {
          email: data.email ?? null,
          fullName: data.fullName ?? profileList[0]?.fullName ?? "",
          location: data.location ?? "",
          phoneNumber: data.phoneNumber,
          players: profileList.map((p) => ({
            _id: p._id,
            dob: p.dob,
            email: null,
            fullName: p.fullName,
            gender: p.gender,
            profilePic: p.profilePic,
            relationshipType: p.relationshipType,
          })),
          user: data.user,
        };

        setState((prev) => ({
          ...prev,
          searchedProfileData: syntheticProfileData,
          isNewUser: false,
          phoneSearchError: null,
          showAddProfileForm: false,
          // Sync registerBy from the sub-form's local selection if Section 2 wasn't picked yet
          registerBy: prev.registerBy || registerBy,
          // Clear new-profile form fields
          newParentName: "",
          newParentGender: "",
          newParentDob: "",
          newPlayerName: "",
          newPlayerGender: "",
          newPlayerDob: "",
          // Do NOT auto-select — let the user pick from the updated player list in Section 3
          // so they can see all profiles including the newly created one.
          selectedPlayer: null,
          selectedAcademy: null,
          selectedSport: "",
          centerList: [],
          selectedCenter: null,
          ageCategoryList: [],
          selectedAgeCategory: "",
          batchList: [],
          selectedBatch: null,
          selectedSlot: null,
          selectedDynamicSlots: [],
          selectedFee: null,
          selectedTrainingFrequency: "",
          selectedPaymentFrequency: "",
          startDate: "",
        }));
      } catch (err: unknown) {
        const anyErr = err as any;
        const body = anyErr?.response?.data;
        const msg =
          body?.error ||
          body?.message ||
          (typeof err === "string" ? err : null) ||
          "Failed to create profile. Please try again.";
        setSubmitError(msg);
      } finally {
        setLoading((l) => ({ ...l, addProfile: false }));
      }
    },
    [state]
  );

  // ── Register-by select ───────────────────────────────────────────────────
  const handleRegisterByChange = useCallback((value: RegisterBy) => {
    setState((prev) => ({
      ...prev,
      registerBy: value,
      selectedPlayer: null,
      selectedAcademy: null,
      selectedSport: "",
      centerList: [],
      selectedCenter: null,
      ageCategoryList: [],
      selectedAgeCategory: "",
      batchList: [],
      selectedBatch: null,
      selectedSlot: null,
        selectedDynamicSlots: [],
      selectedFee: null,
      selectedTrainingFrequency: "",
      selectedPaymentFrequency: "",
      startDate: "",
    }));
  }, []);

  // ── Player select ────────────────────────────────────────────────────────
  const handlePlayerSelect = useCallback((player: PlayerProfile) => {
    setState((prev) => ({
      ...prev,
      selectedPlayer: player,
      selectedAcademy: null,
      selectedSport: "",
      centerList: [],
      selectedCenter: null,
      ageCategoryList: [],
      selectedAgeCategory: "",
      batchList: [],
      selectedBatch: null,
      selectedSlot: null,
        selectedDynamicSlots: [],
      selectedFee: null,
      selectedTrainingFrequency: "",
      selectedPaymentFrequency: "",
      startDate: "",
    }));
  }, []);

  // ── Load academies when player is selected ───────────────────────────────
  useEffect(() => {
    if (mode === "add" && !state.selectedPlayer) return;
    setLoading((l) => ({ ...l, academies: true }));
    listAdminAcademies()
      .then((list) => setState((prev) => ({ ...prev, academyList: list })))
      .catch(() => setState((prev) => ({ ...prev, academyList: [] })))
      .finally(() => setLoading((l) => ({ ...l, academies: false })));
  }, [state.selectedPlayer, mode]);

  // ── Academy select ───────────────────────────────────────────────────────
  const handleAcademySelect = useCallback((academy: AcademyListItem) => {
    const autoSport =
      academy.basicDetails.sports.length === 1 ? academy.basicDetails.sports[0] : "";
    setState((prev) => ({
      ...prev,
      selectedAcademy: academy,
      selectedSport: autoSport,
      centerList: [],
      selectedCenter: null,
      ageCategoryList: [],
      selectedAgeCategory: "",
      batchList: [],
      selectedBatch: null,
      selectedSlot: null,
        selectedDynamicSlots: [],
      selectedFee: null,
      selectedTrainingFrequency: "",
      selectedPaymentFrequency: "",
      startDate: "",
    }));
  }, []);

  const handleSportSelect = useCallback((sport: string) => {
    setState((prev) => ({ ...prev, selectedSport: sport }));
  }, []);

  // ── Load centers when academy selected ───────────────────────────────────
  useEffect(() => {
    if (!state.selectedAcademy) return;
    setLoading((l) => ({ ...l, centers: true }));
    listAdminCenters(state.selectedAcademy._id)
      .then((list) =>
        setState((prev) => ({ ...prev, centerList: list }))
      )
      .catch(() => setState((prev) => ({ ...prev, centerList: [] })))
      .finally(() => setLoading((l) => ({ ...l, centers: false })));
  }, [state.selectedAcademy]);

  // ── Center select ────────────────────────────────────────────────────────
  const handleCenterSelect = useCallback((center: CenterListItem) => {
    setState((prev) => ({
      ...prev,
      selectedCenter: center,
      ageCategoryList: [],
      selectedAgeCategory: "",
      batchList: [],
      selectedBatch: null,
      selectedSlot: null,
        selectedDynamicSlots: [],
      selectedFee: null,
      selectedTrainingFrequency: "",
      selectedPaymentFrequency: "",
      startDate: "",
    }));
  }, []);

  // ── Load age categories and batches when center selected ─────────────────
  useEffect(() => {
    if (!state.selectedCenter) return;
    setLoading((l) => ({ ...l, ageCategories: true }));
    listAgeCategoriesByCenter(state.selectedCenter._id)
      .then((list) => setState((prev) => ({ ...prev, ageCategoryList: list })))
      .catch(() => setState((prev) => ({ ...prev, ageCategoryList: [] })))
      .finally(() => setLoading((l) => ({ ...l, ageCategories: false })));

    setLoading((l) => ({ ...l, batches: true }));
    listAdminBatches(state.selectedCenter._id)
      .then((list) =>
        setState((prev) => ({ ...prev, batchList: list }))
      )
      .catch(() => setState((prev) => ({ ...prev, batchList: [] })))
      .finally(() => setLoading((l) => ({ ...l, batches: false })));
  }, [state.selectedCenter]);

  // ── Age category select ─────────────────────────────────────────────────
  const handleAgeCategorySelect = useCallback((ageCategory: string) => {
    setState((prev) => ({
      ...prev,
      selectedAgeCategory: ageCategory,
      selectedBatch: null,
      selectedSlot: null,
      selectedDynamicSlots: [],
      selectedFee: null,
      selectedTrainingFrequency: "",
      selectedPaymentFrequency: "",
      startDate: "",
    }));
  }, []);

  // ── Batch select ─────────────────────────────────────────────────────────
  const handleBatchSelect = useCallback((batch: BatchListItem) => {
    setState((prev) => ({
      ...prev,
      selectedBatch: batch,
      selectedSlot: null,
        selectedDynamicSlots: [],
      selectedFee: null,
      selectedTrainingFrequency: "",
      selectedPaymentFrequency: "",
      startDate: "",
    }));
  }, []);

  // ── Fixed slot select (single) ────────────────────────────────────────────
  const handleSlotSelect = useCallback((slot: SelectedSlot) => {
    setState((prev) => ({ ...prev, selectedSlot: slot }));
  }, []);

  // ── Dynamic slot toggle (multi-select) ────────────────────────────────────
  const handleDynamicSlotToggle = useCallback((slot: SelectedSlot) => {
    setState((prev) => {
      const exists = prev.selectedDynamicSlots.some(
        (s) => s.day === slot.day && s.startTime === slot.startTime && s.endTime === slot.endTime
      );
      return {
        ...prev,
        selectedDynamicSlots: exists
          ? prev.selectedDynamicSlots.filter(
              (s) =>
                !(s.day === slot.day && s.startTime === slot.startTime && s.endTime === slot.endTime)
            )
          : [...prev.selectedDynamicSlots, slot],
      };
    });
  }, []);

  // ── Training frequency select ─────────────────────────────────────────────
  const handleTrainingFrequencySelect = useCallback(
    (freq: string) => {
      const fee =
        state.selectedBatch?.fees.find((f) => f.trainingFrequency === freq) ?? null;
      setState((prev) => ({
        ...prev,
        selectedTrainingFrequency: freq,
        selectedFee: fee,
        selectedPaymentFrequency: "",
      }));
    },
    [state.selectedBatch]
  );

  // ── Payment frequency select ─────────────────────────────────────────────
  const handlePaymentFrequencySelect = useCallback((freq: string) => {
    setState((prev) => ({ ...prev, selectedPaymentFrequency: freq }));
  }, []);

  // ── Start date ───────────────────────────────────────────────────────────
  const handleStartDateChange = useCallback((date: string) => {
    setState((prev) => ({ ...prev, startDate: date }));
  }, []);

  // ── Payment amount ────────────────────────────────────────────────────────
  const handlePaymentAmountChange = useCallback((v: string) => {
    setState((prev) => ({ ...prev, paymentAmount: v }));
  }, []);

  // ── New-profile field setters ─────────────────────────────────────────────
  const setNewParentName = (v: string) => setState((p) => ({ ...p, newParentName: v }));
  const setNewParentGender = (v: string) => setState((p) => ({ ...p, newParentGender: v }));
  const setNewParentDob = (v: string) => setState((p) => ({ ...p, newParentDob: v }));
  const setNewPlayerName = (v: string) => setState((p) => ({ ...p, newPlayerName: v }));
  const setNewPlayerGender = (v: string) => setState((p) => ({ ...p, newPlayerGender: v }));
  const setNewPlayerDob = (v: string) => setState((p) => ({ ...p, newPlayerDob: v }));
  const setShowAddProfileForm = (v: boolean) =>
    setState((p) => ({ ...p, showAddProfileForm: v }));

  const setPhoneNumber = (v: string) =>
    setState((p) => ({
      ...p,
      phoneNumber: v,
      phoneSearched: false,
      searchedProfileData: null,
      isNewUser: false,
      phoneSearchError: null,
      showAddProfileForm: false,
      registerBy: "",
      selectedPlayer: null,
    }));

  // ── Validation ────────────────────────────────────────────────────────────
  const validateForSubmit = (): string | null => {
    const slotType =
      mode === "add"
        ? state.selectedBatch?.slotType ?? "fixed"
        : state.selectedBatch?.slotType ?? initialEnrolment?.slotType ?? "fixed";

    if (mode === "add") {
      if (!state.searchedProfileData) return "Please search for a phone number first.";
      if (!state.registerBy) return "Please select who is registering.";
      if (!state.selectedPlayer) return "Please select a player.";
      if (!state.selectedAcademy) return "Please select an academy.";
      if (!state.selectedSport) return "Please select a sport.";
      if (!state.selectedCenter) return "Please select a center.";
      if (!state.selectedAgeCategory) return "Please select an age category.";
      if (!state.selectedBatch) return "Please select a batch.";
      if (state.selectedBatch.ageCategory !== state.selectedAgeCategory)
        return "Selected batch does not belong to the selected age category.";
      if (slotType === "dynamic") {
        if (state.selectedDynamicSlots.length === 0) return "Please select at least one slot.";
      } else {
        if (!state.selectedSlot) return "Please select a slot.";
      }
      if (!state.selectedTrainingFrequency) return "Please select a training frequency.";
      if (!state.selectedPaymentFrequency) return "Please select a payment frequency.";
      if (!state.startDate) return "Please select a start date.";
      const addAmt = parseFloat(state.paymentAmount);
      if (!state.paymentAmount || isNaN(addAmt) || addAmt <= 0)
        return "Please enter a valid payment amount.";
    }
    if (mode === "edit") {
      if (!state.selectedAcademy) return "Please select an academy.";
      if (!state.selectedCenter) return "Please select a center.";
      if (!state.selectedAgeCategory) return "Please select an age category.";
      if (!state.selectedBatch) return "Please select a batch.";
      if (state.selectedBatch.ageCategory !== state.selectedAgeCategory)
        return "Selected batch does not belong to the selected age category.";
      if (!state.selectedTrainingFrequency) return "Please select a training frequency.";
      if (!state.selectedPaymentFrequency) return "Please select a payment frequency.";
      if (!state.startDate) return "Please select a start date.";
      const editAmt = parseFloat(state.paymentAmount);
      if (!state.paymentAmount || isNaN(editAmt) || editAmt <= 0)
        return "Please enter a valid payment amount.";
    }
    return null;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = useCallback(async (): Promise<boolean> => {
    const validationError = validateForSubmit();
    if (validationError) {
      setSubmitError(validationError);
      return false;
    }

    setLoading((l) => ({ ...l, submit: true }));
    setSubmitError(null);

    try {
      if (mode === "add") {
        const s = state;
        const batch = s.selectedBatch!;
        const slotType = batch.slotType;

        const payload: CreateEnrolmentV2Payload = {
          enrolledThrough: "Excel",
          enrolledBy: s.registerBy as "self" | "parent",
          user: s.searchedProfileData!.user,
          player: s.selectedPlayer!._id,
          academy: s.selectedAcademy!._id,
          coachingCenter: s.selectedCenter!._id,
          batch: batch._id,
          batchName: batch.batchName,
          course: batch.course,
          ageCategory: s.selectedAgeCategory,
          slotType: slotType as "fixed" | "dynamic",
          sports: s.selectedSport,
          location: s.selectedCenter!.basicDetails.location,
          startDate: s.startDate,
          trainingFrequency: s.selectedTrainingFrequency,
          paymentFrequency: s.selectedPaymentFrequency,
          paymentAmount: parseFloat(s.paymentAmount),
        };

        if (slotType === "fixed" && s.selectedSlot) {
          payload.fixedSlots = [
            {
              days: s.selectedSlot.days ?? [],
              startTime: s.selectedSlot.startTime,
              endTime: s.selectedSlot.endTime,
            },
          ];
        } else if (slotType === "dynamic" && s.selectedDynamicSlots.length > 0) {
          payload.slots = s.selectedDynamicSlots.map((sl) => ({
            day: sl.day ?? "",
            startTime: sl.startTime,
            endTime: sl.endTime,
          }));
        }

        await addEnrolmentV2(payload);
      } else if (mode === "edit" && initialEnrolment) {
        const s = state;
        const batch = s.selectedBatch!;
        const slotType = batch.slotType;

        const payload: UpdateEnrolmentV2Payload = {
          academy: s.selectedAcademy!._id,
          coachingCenter: s.selectedCenter!._id,
          batch: batch._id,
          ageCategory: s.selectedAgeCategory,
          slotType: slotType as "fixed" | "dynamic",
          trainingFrequency: s.selectedTrainingFrequency,
          paymentFrequency: s.selectedPaymentFrequency,
          startDate: s.startDate,
          paymentAmount: parseFloat(s.paymentAmount),
        };

        if (slotType === "fixed" && s.selectedSlot) {
          payload.fixedSlots = [
            {
              days: s.selectedSlot.days ?? [],
              startTime: s.selectedSlot.startTime,
              endTime: s.selectedSlot.endTime,
            },
          ];
        } else if (slotType === "dynamic" && s.selectedDynamicSlots.length > 0) {
          payload.slots = s.selectedDynamicSlots.map((sl) => ({
            day: sl.day ?? "",
            startTime: sl.startTime,
            endTime: sl.endTime,
          }));
        }

        await updateEnrolmentV2(initialEnrolment._id, payload);
      }

      onSuccess?.();
      return true;
    } catch (err: unknown) {
      const anyErr = err as any;
      const body = anyErr?.response?.data;
      const msg =
        body?.error ||
        body?.message ||
        (typeof err === "string" ? err : null) ||
        "Failed to submit enrolment. Please try again.";
      setSubmitError(msg);
      return false;
    } finally {
      setLoading((l) => ({ ...l, submit: false }));
    }
  }, [mode, state, initialEnrolment, onSuccess]);

  // ── Derived helpers ────────────────────────────────────────────────────────
  const availablePaymentFrequencies = state.selectedFee
    ? sortAvailablePaymentFrequencies(
        (Object.entries(state.selectedFee.feeInfo) as [string, number | null][])
          .filter(([, v]) => v !== null)
          .map(([k]) => k),
        planFields
      )
    : [];

  const filteredBatchList = state.selectedAgeCategory
    ? state.batchList.filter((batch) => batch.ageCategory === state.selectedAgeCategory)
    : state.batchList;

  const filteredPlayers: PlayerProfile[] = (() => {
    if (!state.searchedProfileData || !state.registerBy) return [];
    if (state.registerBy === "self") {
      return state.searchedProfileData.players.filter((p) => p.relationshipType === "self");
    }
    return state.searchedProfileData.players.filter((p) => p.relationshipType === "parent");
  })();

  return {
    state,
    loading,
    submitError,
    setSubmitError,
    filteredPlayers,
    filteredBatchList,
    availablePaymentFrequencies,
    planFields,
    handlers: {
      setPhoneNumber,
      handlePhoneSearch,
      handleAddProfile,
      setShowAddProfileForm,
      setNewParentName,
      setNewParentGender,
      setNewParentDob,
      setNewPlayerName,
      setNewPlayerGender,
      setNewPlayerDob,
      handleRegisterByChange,
      handlePlayerSelect,
      handleAcademySelect,
      handleSportSelect,
      handleCenterSelect,
      handleAgeCategorySelect,
      handleBatchSelect,
      handleSlotSelect,
      handleDynamicSlotToggle,
      handleTrainingFrequencySelect,
      handlePaymentFrequencySelect,
      handleStartDateChange,
      handlePaymentAmountChange,
      handleSubmit,
    },
  };
}
